import { useMemo, useState } from 'react';
import {
  Button,
  Autocomplete,
  Menu,
  TextField,
  Box,
  InputAdornment,
} from '@mui/material';
import { squareFeetToSizeOptions } from '../../../../utils/search.ts';
import { formatLargeNumber } from '../../../../utils/number-formatting.ts';

const menuStyles = {
  '& .MuiPaper-root': {
    width: '400px',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    justifyContent: 'center',
    alignItems: 'center',
    ul: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },
};

// TODO: Remove this in favor of listed items in a types file alongside Valibot validation and types
const dropdownOptions = [
  { label: '1,000 sqft', id: 1000 },
  { label: '2,000 sqft', id: 2000 },
  { label: '3,000 sqft', id: 3000 },
  { label: '4,000 sqft', id: 4000 },
  { label: '5,000 sqft', id: 5000 },
  { label: '7,500 sqft', id: 7500 },
  { label: '1/4 acre', id: 10890 },
  { label: '1/2 acre', id: 21780 },
  { label: '1 acre', id: 43560 },
  { label: '2 acres', id: 87120 },
  { label: '5 acres', id: 217800 },
  { label: '10 acres', id: 435600 },
  { label: '20 acres', id: 871200 },
  { label: '50 acres', id: 2178002 },
  { label: '100 acres', id: 4356005 },
];

interface SizeDropdownProps {
  onMaxSizeChange: (val: number | undefined) => void;
  onMinSizeChange: (val: number | undefined) => void;
  maxSize: number | undefined;
  minSize: number | undefined;
}

export const SizeDropdown = ({
  onMaxSizeChange,
  onMinSizeChange,
  minSize,
  maxSize,
}: SizeDropdownProps) => {
  const dropdownText = useMemo(() => {
    if (!minSize && !maxSize) {
      return 'Size';
    } else if (minSize && !maxSize) {
      if (!minSize) {
        return 'No Minimum Size';
      }
      return `More than ${squareFeetToSizeOptions(minSize.toString())}`;
    } else if (maxSize && !minSize) {
      if (!maxSize) {
        return 'No Maximum Size';
      }
      return `Less than ${squareFeetToSizeOptions(maxSize.toString())}`;
    } else if (maxSize && minSize) {
      return `${squareFeetToSizeOptions(minSize.toString())} - ${squareFeetToSizeOptions(maxSize.toString())}`;
    }
  }, [maxSize, minSize]);

  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [minFocused, setMinFocused] = useState(false);
  const showMinAddornment = minFocused || !!minSize;

  const [maxFocused, setMaxFocused] = useState(false);
  const showMaxAddornment = maxFocused || !!maxSize;

  return (
    <div className={`size-dropdown-container`}>
      <Button
        data-open-size-dropdown
        data-open-size-dropdown-has-value={minSize || maxSize ? true : false}
        id="open-size-dropdown-button"
        aria-controls={open ? 'basic-size-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
        variant="filled"
        ref={setAnchorEl}
      >
        {dropdownText}
      </Button>
      <Menu
        sx={menuStyles}
        id="basic-size-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          list: {
            'aria-labelledby': 'open-size-dropdown-button',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Autocomplete
            fullWidth
            data-size-min-input
            freeSolo
            inputValue={minSize ? formatLargeNumber(minSize) : ''}
            options={[{ label: 'No Min Size', id: -1 }, ...dropdownOptions]}
            onInputChange={(_event, newValue) => {
              // Remove all non-numeric characters from the input
              // and set the max value to the new value
              const newVal =
                dropdownOptions.find((option) => option.label === newValue)
                  ?.id ?? Number(newValue.replace(/\D/g, ''));
              if (Number.isNaN(newVal)) {
                onMinSizeChange(undefined);
              } else {
                onMinSizeChange(newVal);
              }
            }}
            onChange={(_event, newValue) => {
              if (typeof newValue === 'string') {
                const newVal =
                  dropdownOptions.find((option) => option.label === newValue)
                    ?.id ?? Number(newValue.replace(/\D/g, ''));
                if (Number.isNaN(newVal)) {
                  onMinSizeChange(undefined);
                } else {
                  onMinSizeChange(newVal);
                }
              } else {
                let newValueId = newValue ? newValue.id : undefined;
                if (newValueId === -1) {
                  newValueId = undefined; // Convert "No Min Size" to undefined
                }
                onMinSizeChange(newValueId);
              }
            }}
            onFocus={() => setMinFocused(true)}
            onBlur={() => setMinFocused(false)}
            renderInput={(params) => (
              <TextField
                sx={{ padding: 0 }}
                {...params}
                label="Minimum Size"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: showMinAddornment ? (
                      <InputAdornment position="end">sqft</InputAdornment>
                    ) : null,
                  },
                }}
              />
            )}
          />
          <Box sx={{ margin: '12px' }} data-size-input-dash-container>
            <span data-size-input-dash>-</span>
          </Box>
          <Autocomplete
            fullWidth
            data-size-max-input
            freeSolo
            inputValue={maxSize ? formatLargeNumber(maxSize) : ''}
            options={[{ label: 'No Max Size', id: -1 }, ...dropdownOptions]}
            onInputChange={(_event, newValue) => {
              // Remove all non-numeric characters from the input
              // and set the max value to the new value
              const newVal =
                dropdownOptions.find((option) => option.label === newValue)
                  ?.id ?? Number(newValue.replace(/\D/g, ''));
              if (Number.isNaN(newVal)) {
                onMaxSizeChange(undefined);
              } else {
                onMaxSizeChange(newVal);
              }
            }}
            onChange={(_event, newValue) => {
              if (typeof newValue === 'string') {
                const newVal =
                  dropdownOptions.find((option) => option.label === newValue)
                    ?.id ?? Number(newValue.replace(/\D/g, ''));
                if (Number.isNaN(newVal)) {
                  onMaxSizeChange(undefined);
                } else {
                  onMaxSizeChange(newVal);
                }
              } else {
                let newValueId = newValue ? newValue.id : undefined;
                if (newValueId === -1) {
                  newValueId = undefined; // Convert "No Max Size" to undefined
                }
                onMaxSizeChange(newValueId);
              }
            }}
            onFocus={() => setMaxFocused(true)}
            onBlur={() => setMaxFocused(false)}
            renderInput={(params) => (
              <TextField
                sx={{ padding: 0 }}
                {...params}
                label="Maximum Size"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: showMaxAddornment ? (
                      <InputAdornment position="end">sqft</InputAdornment>
                    ) : null,
                  },
                }}
              />
            )}
          />
        </Box>
      </Menu>
    </div>
  );
};
